import { forwardRef } from 'react';
import clsx from 'clsx';

// `brand-600` is a near-black navy, so hover as a colour step alone measures 1.14:1
// and reads as no feedback at all. Elevation and a 1px press carry it instead.
const variants = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700 hover:shadow-md hover:shadow-brand-900/25 active:bg-brand-800 active:shadow-sm active:translate-y-px',
  secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:border-slate-400 hover:text-slate-900 hover:shadow-sm active:bg-slate-100 active:shadow-none active:translate-y-px',
  danger: 'bg-danger-500 text-white shadow-sm hover:bg-danger-700 hover:shadow-md hover:shadow-danger-700/25 active:bg-danger-700 active:shadow-sm active:translate-y-px',
  ghost: 'text-slate-600 hover:bg-slate-100 active:bg-slate-200',
  accent: 'bg-accent-500 text-white shadow-sm hover:bg-accent-600 hover:shadow-md hover:shadow-accent-900/25 active:bg-accent-700 active:shadow-sm active:translate-y-px',
};

const sizes = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
};

const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  loading,
  className,
  type = 'button',
  ...props
}, ref) => (
  <button
    ref={ref}
    type={type}
    className={clsx(
      // `transition` rather than `transition-colors`: the variants above lean on
      // shadow and a 1px press, which colour-only transitions would leave jumpy.
      'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 disabled:shadow-none',
      variants[variant],
      sizes[size],
      // Consumer className is merged last so it wins on class-attribute order.
      className
    )}
    disabled={loading || props.disabled}
    aria-busy={loading ? 'true' : undefined}
    {...props}
  >
    {loading && (
      // The transparent gap only reads as a spinner while it turns: without motion
      // it has to close, or a reduced-motion user watches a broken ring instead.
      <div className="motion-safe:animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent motion-reduce:border-t-current" />
    )}
    {children}
  </button>
));

Button.displayName = 'Button';
export default Button;
